import { createContext, useState, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { TUserSchema } from '@/schemas/user/user.schema';
import { useGetUserById } from '@/hooks/user.hooks';
import { useGetUserRolesData } from '@/hooks/user-entity-role.hooks';
import { RoleName } from '@/api/entities/user-role-type';
import {
  getAccessToken,
  setAccessToken,
  clearAccessToken,
  hasSession,
} from '@/utils/token.utils';
import { refreshToken } from '@/api/auth/auth';

type AuthContextType = {
  loggedInUser?: TUserSchema;
  setLoggedInUser: Dispatch<SetStateAction<TUserSchema | undefined>>;
  primaryRole?: RoleName;
  isLimitedRole?: boolean;
  limitedRoleLoggedInUserId?: string;
  isAuthReady: boolean;
  isRestoring: boolean;
};

export const AuthContext = createContext<AuthContextType | null>(null);

type AuthProviderType = {
  children: React.ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderType) => {
  const [loggedInUser, setLoggedInUser] = useState<TUserSchema>();
  const [userId, setUserId] = useState<string | undefined>();
  const [isRestoring, setIsRestoring] = useState(true);

  // On mount, try to restore session via refresh token cookie
  useEffect(() => {
    const restore = async () => {
      try {
        const response = await refreshToken();
        const { access_token, user } = response.data;
        setAccessToken(access_token);
        setUserId(user.id);
        setLoggedInUser(user);
      } catch {
        clearAccessToken();
      } finally {
        setIsRestoring(false);
      }
    };

    if (!getAccessToken() && hasSession()) {
      restore();
    } else {
      setIsRestoring(false);
    }
  }, []);

  const user = useGetUserById(userId);
  const { roles, isLoadingRoles } = useGetUserRolesData(userId);

  // Get primary role
  const primaryRole = roles?.includes(RoleName.ADMIN)
    ? RoleName.ADMIN
    : (roles[0] as RoleName) || (sessionStorage.getItem('role') as RoleName);

  /**
   * Prepare limited rights logged in user id
   * Use userId directly instead of waiting for loggedInUser to be populated
   */
  const isAdmin = primaryRole === RoleName.ADMIN;
  const isModerator = primaryRole === RoleName.MODERATOR;
  const limitedRoleLoggedInUserId = isAdmin || isModerator ? undefined : userId;
  /**
   * END OF Prepare limited rights logged in user id
   */

  // Keep userId in sync when loggedInUser is set externally (Login page, login-as-user).
  // Reset it on logout so the previous user's roles query stops running and can no
  // longer re-populate the persisted role.
  useEffect(() => {
    setUserId(loggedInUser?.id);
  }, [loggedInUser?.id]);

  // Set loggedInUser when fetched user is available
  useEffect(() => {
    if (user?.data?.data) {
      setLoggedInUser(user.data.data);
    }
  }, [user?.data?.data]);

  // Auth is fully ready when session restore is done and roles have been resolved
  const isAuthReady =
    !isRestoring &&
    (!loggedInUser || // not logged in — nothing to load
      (!!userId && !isLoadingRoles)); // logged in and roles query settled

  // Keep user role in session storage for persistence for fallback.
  // Only act once the current user's roles have actually resolved, so we never
  // leak the previous user's role and we clear it for a user that has no roles.
  // Guarding on isLoadingRoles avoids wiping the fallback during a page reload.
  useEffect(() => {
    if (!userId || isLoadingRoles) return;

    if (roles.length > 0) {
      sessionStorage.setItem('role', roles[0] as RoleName);
    } else {
      sessionStorage.removeItem('role');
    }
  }, [userId, isLoadingRoles, roles]);

  return (
    <AuthContext.Provider
      value={{
        loggedInUser,
        setLoggedInUser,
        primaryRole,
        isLimitedRole: !isAdmin && !isModerator,
        limitedRoleLoggedInUserId,
        isAuthReady,
        isRestoring,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
